import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, ConfigProvider, Flex, Button, Drawer } from 'antd';
import UserIconDropdown from './common/components/userIconDropdown/UserIconDropdown';
import LeftMenu from './common/components/LeftMenu/LeftMenu';
import { useAuth } from './context/AuthContext';
import AppLoader from './common/components/AppLoader';
import MarketingConsentPrompt from './components/marketingConsent/MarketingConsentPrompt';
import { routesPath } from './common/constants/routesPath';
import globalRouter from './routes/GlobalRouter';
import { spacing, iconColor, colors } from './theming';
import { useMedia } from './hooks/useMediaQuery';
import useSupportChat from './hooks/useSupportChat';
import MobileMenu from './common/components/MobileMenu/MobileMenu';
import logo from './assets/check-vin-logo.png';
import icon from './common/styles/Icon.module.scss';
import { themeConfig } from './theming/themeConfig';
import './App.scss';
import { useSSR, useTranslation } from 'react-i18next';
import { TeamsProvider } from './context/TeamsContext';
import { ScrollProvider } from './context/ScrollContext';
import { CreditsProvider } from './context/CreditsContext';
import { LanguageSelect } from './common/components/languageSelect/languageSelect';
import ReactGA from 'react-ga4';
import GeneralFooter from './common/components/Footer/Footer';
import classes from './components/vinCode/styles.module.scss';

const { Header, Content, Sider } = Layout;

const HEADER_HEIGHT_DESKTOP = '89px'; // we need this value to calculate sidebar height
const HEADER_HEIGHT_TABLET = '72px';
const HEADER_HEIGHT_MOBILE = '64px';

const hiddenSiderRoutes = [
    routesPath.ROOT.PAYMENT,
    routesPath.ROOT.REPORT,
    routesPath.ROOT.INTERNAL_REPORT,
    routesPath.ROOT.COMBINED_REPORT,
];
const fullScreenRoutes = [routesPath.ROOT.INTERNAL_REPORT, routesPath.ROOT.COMBINED_REPORT];

function App() {
    const { i18n, t } = useTranslation(['navigationLinks']);

    useSSR(window.initialI18nStore, i18n.language);

    const [openMobileMenu, setOpenMobileMenu] = useState(false);

    const { isSmallDevice, isExtraSmallDevice } = useMedia();
    const { fetchUser, isUserLoading, logout } = useAuth();

    const [isDrawerOpen, setItDrawerOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    globalRouter.navigate = navigate;

    const hideSider = hiddenSiderRoutes.some(route => {
        // Handle dynamic routes by checking if the current path starts with the route base
        if (route.includes(':')) {
            const baseRoute = route.split(':')[0];
            return location.pathname.startsWith(baseRoute);
        }
        return location.pathname === route;
    });

    const isFullScreenPage = fullScreenRoutes.some(route => {
        if (route.includes(':')) {
            const baseRoute = route.split(':')[0];
            return location.pathname.startsWith(baseRoute);
        }
        return location.pathname === route;
    });

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    useEffect(() => {
        if (location.pathname === routesPath.ROOT.PATH) {
            navigate(routesPath.ROOT.DASHBOARD);
        }
    }, [location, navigate]);

    useEffect(() => {
        const parts = location.pathname.split('/').filter(Boolean);

        ReactGA.send({
            hitType: 'pageview',
            page_title: document.title,
            page_path: `/${parts[0]}`,
        });
    }, [location.pathname]);

    useEffect(() => {
        openMobileMenu ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'auto');
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [openMobileMenu]);

    const closeMobileMenu = () => {
        setOpenMobileMenu(false);
        setItDrawerOpen(false);
    };

    useSupportChat();

    return !isUserLoading ? (
        <ConfigProvider theme={themeConfig}>
            <TeamsProvider>
                <ScrollProvider>
                    <CreditsProvider>
                        <Layout className="main-wrapper" style={{ overflow: isFullScreenPage ? 'visible' : 'hidden' }}>
                            <Layout style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Header
                                    className="header"
                                    style={{
                                        width: '100%',
                                        borderBottom: '1px solid #E8E8EB',
                                        height: isExtraSmallDevice
                                            ? HEADER_HEIGHT_MOBILE
                                            : isSmallDevice
                                              ? HEADER_HEIGHT_TABLET
                                              : HEADER_HEIGHT_DESKTOP,
                                        paddingBlock: isSmallDevice
                                            ? spacing[3]
                                            : isExtraSmallDevice
                                              ? spacing[2]
                                              : spacing[4],
                                        paddingInline: isSmallDevice ? spacing[5] : isExtraSmallDevice ? spacing[3] : 0,
                                    }}
                                >
                                    <div className="notifications-user">
                                        {isSmallDevice && (
                                            <>
                                                <Button
                                                    type="link"
                                                    size="large"
                                                    onClick={() => {
                                                        setItDrawerOpen(prev => !prev);
                                                        setOpenMobileMenu(false);
                                                    }}
                                                >
                                                    {isDrawerOpen ? (
                                                        <i
                                                            className={`fi fi-sr-cross-small ${icon.md}`}
                                                            style={{ marginTop: 0, color: iconColor.primary }}
                                                        />
                                                    ) : (
                                                        <i
                                                            className={`fi fi-rr-bars-staggered ${icon.md}`}
                                                            style={{ marginTop: 0, color: iconColor.primary }}
                                                        />
                                                    )}
                                                </Button>
                                                <MobileMenu
                                                    isMobileMenuOpen={openMobileMenu}
                                                    toggleMenu={setOpenMobileMenu}
                                                    closeMobileMenu={closeMobileMenu}
                                                    headerHeight={
                                                        isExtraSmallDevice
                                                            ? HEADER_HEIGHT_MOBILE
                                                            : isSmallDevice
                                                              ? HEADER_HEIGHT_TABLET
                                                              : HEADER_HEIGHT_DESKTOP
                                                    }
                                                    logout={logout}
                                                />
                                            </>
                                        )}

                                        <Flex align="center">
                                            <Link
                                                to={routesPath.ROOT.DASHBOARD}
                                                style={{ lineHeight: 0 }}
                                                onClick={() => {
                                                    setItDrawerOpen(false);
                                                    setOpenMobileMenu(false);
                                                }}
                                            >
                                                <img
                                                    alt={'CheckVIN.ai'}
                                                    src={logo}
                                                    style={{
                                                        width: isExtraSmallDevice ? 120 : isSmallDevice ? 150 : 170,
                                                    }}
                                                />
                                            </Link>
                                        </Flex>

                                        {/* uncomment next block if needed, in future */}
                                        {/* {!isSmallDevice && <NavigationLinks isMobile={false} t={t} />} */}

                                        <Flex gap={spacing[3]} align="center">
                                            {!isSmallDevice && (
                                                <Button
                                                    type="primary"
                                                    size="middle"
                                                    className={classes.sampleReportButton}
                                                    onClick={() => {
                                                        window.open(
                                                            routesPath.PUBLIC.COMBINED_REPORT_EXAMPLE,
                                                            '_blank',
                                                            'noopener,noreferrer'
                                                        );
                                                    }}
                                                >
                                                    {t('sample-report')}
                                                </Button>
                                            )}

                                            {!isSmallDevice && <LanguageSelect i18n={i18n} />}

                                            {!isSmallDevice && <UserIconDropdown />}

                                            {isSmallDevice && (
                                                <Button
                                                    onClick={() => {
                                                        setOpenMobileMenu(prev => !prev);
                                                        setItDrawerOpen(false);
                                                    }}
                                                    style={{ border: 0, outline: 0, boxShadow: 'none' }}
                                                >
                                                    <i
                                                        className={`fi fi-rr-user ${icon.md}`}
                                                        style={{ marginTop: 0, color: iconColor.primary }}
                                                    ></i>
                                                </Button>
                                            )}
                                        </Flex>
                                    </div>
                                </Header>

                                <Content
                                    className="content"
                                    style={{
                                        width: '100%',
                                        padding: 0,
                                    }}
                                >
                                    <Flex
                                        style={{
                                            maxWidth: isFullScreenPage ? '100%' : '1440px',
                                            margin: isFullScreenPage ? 0 : '0 auto',
                                            minHeight: `calc(100vh - ${
                                                isExtraSmallDevice
                                                    ? HEADER_HEIGHT_MOBILE
                                                    : isSmallDevice
                                                      ? HEADER_HEIGHT_TABLET
                                                      : HEADER_HEIGHT_DESKTOP
                                            })`,
                                        }}
                                    >
                                        {!isSmallDevice && !hideSider && (
                                            <Sider
                                                width={'auto'}
                                                trigger={null}
                                                style={{
                                                    flex: 'none',
                                                    overflow: 'visible',
                                                    borderInline: '1px solid #E8E8EB',
                                                    height: 'auto',
                                                }}
                                            >
                                                <LeftMenu
                                                    headerHeight={
                                                        isExtraSmallDevice
                                                            ? HEADER_HEIGHT_MOBILE
                                                            : isSmallDevice
                                                              ? HEADER_HEIGHT_TABLET
                                                              : HEADER_HEIGHT_DESKTOP
                                                    }
                                                />
                                            </Sider>
                                        )}

                                        {isSmallDevice && (
                                            <Drawer
                                                width={'auto'}
                                                placement="left"
                                                closable
                                                onClose={() => setItDrawerOpen(false)}
                                                open={isDrawerOpen}
                                                height={`calc(100vh - ${
                                                    isExtraSmallDevice
                                                        ? HEADER_HEIGHT_MOBILE
                                                        : isSmallDevice
                                                          ? HEADER_HEIGHT_TABLET
                                                          : HEADER_HEIGHT_DESKTOP
                                                })`}
                                                rootStyle={{
                                                    // the impersonation bar, when there is one, sits above the header
                                                    top: `calc(${
                                                        isExtraSmallDevice
                                                            ? HEADER_HEIGHT_MOBILE
                                                            : isSmallDevice
                                                              ? HEADER_HEIGHT_TABLET
                                                              : HEADER_HEIGHT_DESKTOP
                                                    } + var(--impersonation-bar-height, 0px))`,
                                                    height: `calc(100vh - ${
                                                        isExtraSmallDevice
                                                            ? HEADER_HEIGHT_MOBILE
                                                            : isSmallDevice
                                                              ? HEADER_HEIGHT_TABLET
                                                              : HEADER_HEIGHT_DESKTOP
                                                    } - var(--impersonation-bar-height, 0px))`,
                                                }}
                                                styles={{
                                                    header: { display: 'none' },
                                                    body: {
                                                        padding: 0,

                                                        overflow: 'auto',
                                                        borderRadius: 0,
                                                    },
                                                }}
                                            >
                                                <LeftMenu
                                                    headerHeight={
                                                        isExtraSmallDevice
                                                            ? HEADER_HEIGHT_MOBILE
                                                            : isSmallDevice
                                                              ? HEADER_HEIGHT_TABLET
                                                              : HEADER_HEIGHT_DESKTOP
                                                    }
                                                    closeDrawer={() => setItDrawerOpen(false)}
                                                />
                                            </Drawer>
                                        )}

                                        <div
                                            style={{
                                                width: '100%',
                                                paddingBlock: isFullScreenPage
                                                    ? 0
                                                    : isExtraSmallDevice
                                                      ? '20px'
                                                      : isSmallDevice
                                                        ? spacing[5]
                                                        : spacing[7],
                                                paddingInline: isFullScreenPage
                                                    ? 0
                                                    : isExtraSmallDevice
                                                      ? spacing[3]
                                                      : isSmallDevice
                                                        ? spacing[4]
                                                        : spacing[7],
                                                backgroundColor: hideSider ? colors.white : '#F6F6F8',
                                                overflow: isFullScreenPage ? 'visible' : 'hidden',
                                            }}
                                        >
                                            <Outlet />
                                        </div>
                                        <MarketingConsentPrompt />
                                    </Flex>

                                    <GeneralFooter />
                                </Content>
                            </Layout>
                        </Layout>
                    </CreditsProvider>
                </ScrollProvider>
            </TeamsProvider>
        </ConfigProvider>
    ) : (
        <AppLoader />
    );
}

export default App;
